import Service from './Service';
import {
  AnswerResult,
  BulkActionResult,
  CardLayout,
  DayForecast,
  DeckCount,
  DueQueue,
  Rating,
  ReviewCard,
  ReviewCardsPage,
  ReviewFacets,
  ReviewHistoryEntry,
  ReviewMask,
  ReviewSessionSummary,
  ReviewStats,
  SubjectCount,
} from '../@types/Revisao';

export interface CreateQuestionCardBody {
  source: 'question';
  questionId: string;
  subjectId: string;
  tags?: string[];
}

export interface CreateFlashcardBody {
  source: 'flashcard';
  layout: CardLayout;
  front?: string;
  back?: string;
  frontImageKey?: string;
  masks?: ReviewMask[];
  /**
   * Nome que `POST /review/cards` lê (`hide_all` | `hide_one`). O handler não
   * tem campo `studyMode` — esse é o vocabulário da tela, não o do fio.
   */
  occlusionMode?: 'hide_all' | 'hide_one';
  subjectId: string;
  tags?: string[];
}

export type CreateCardBody = CreateQuestionCardBody | CreateFlashcardBody;

export interface UpdateCardBody {
  layout?: CardLayout;
  front?: string;
  back?: string;
  frontImageKey?: string;
  masks?: ReviewMask[];
  /**
   * Nome que `PUT /review/cards/:id` lê (`hide_all` | `hide_one`).
   * O handler não tem campo `studyMode` — esse é o vocabulário da tela, não o
   * do fio. Omitir deixa o modo já gravado como está.
   */
  occlusionMode?: 'hide_all' | 'hide_one';
  subjectId?: string;
  tags?: string[];
}

export interface GetDueParams {
  limit?: number;
  subjectId?: string;
  /** Matéria list to include (any of). */
  subjectIds?: string[];
  tag?: string;
  /** Deck list (tags) to include — e.g. today's decks from the weekly schedule. */
  tags?: string[];
  /** Study upcoming cards before they are due (anticipate a deck's reviews). */
  ahead?: boolean;
  /** Bound anticipation: only cards due on or before this instant (ISO string). */
  dueBefore?: string;
}

export interface SessionSummaryParams {
  /** ISO-8601 instant. Start of the window the summary is aggregated over. */
  from: string;
  /** ISO-8601 instant. End of that window. */
  to: string;
}

export interface ListTemplatesParams {
  /** Sent as the query key `q`. Matches content, subject name, tags and the short id. */
  q?: string;
  layout?: CardLayout;
  subjectId?: string;
  tag?: string;
  /** 1-based. Sent only when the caller passes it. */
  page?: number;
  pageSize?: number;
}

/** `moveSubject` is the wire action: it moves the template and the copies materialized
 *  from it. `move` is not a value the route accepts. */
export type BulkCardsRequest =
  | { action: 'moveSubject'; cardIds: string[]; subjectId: string }
  | { action: 'addTag'; cardIds: string[]; tag: string }
  | { action: 'duplicate'; cardIds: string[] }
  | { action: 'delete'; cardIds: string[] };

const RATINGS = ['again', 'hard', 'good', 'easy'] as const;

type TemplateRow = ReviewCardsPage['items'][number];

/** JSON objects only. Arrays are a different success body and must not fall through. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** `0` stays `0`. Null, a missing field and a string stay absent — never coerced to 0. */
function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

class ReviewService extends Service {
  static createCard(body: CreateCardBody) {
    return this.Http.post<{ card_id: string; status: string }>(
      '/review/cards',
      body
    ).then(this.getData);
  }

  /** Next `days` days, each with the decks (tags) coming due that day + counts. */
  static getForecast(days = 7) {
    return this.Http.get<DayForecast[]>('/review/forecast', { params: { days } }).then(
      this.getData
    );
  }

  static getDue(params: GetDueParams = {}) {
    const { tags, subjectIds, ...rest } = params;
    const query: Record<string, unknown> = { ...rest };
    // Go reads a single query value, so send lists comma-separated.
    if (tags && tags.length) query.tags = tags.join(',');
    if (subjectIds && subjectIds.length) query.subjectIds = subjectIds.join(',');
    return this.Http.get<DueQueue>('/review/due', { params: query }).then(this.getData);
  }

  static answer(id: string, rating: Rating, elapsedMs: number) {
    return this.Http.post<AnswerResult>(`/review/cards/${id}/answer`, {
      rating,
      elapsedMs,
    }).then(this.getData);
  }

  /** Drops the last answer and returns the card as it stands afterwards.
   *  The API refuses a card with nothing to undo, and a card owned by someone else;
   *  this method does not translate that rejection. */
  static undo(id: string) {
    return this.Http.post<ReviewCard>(`/review/cards/${id}/undo`).then(this.getData);
  }

  static listCards() {
    return this.Http.get<ReviewCard[]>('/review/cards').then(this.getData);
  }

  /** One card's answer log. The body is `{"history":[...]}` (or a raw array).
   *  `[]` is a real empty history; a 404 still rejects. `elapsedMs: null` stays
   *  off the entry. This does not invent entries. */
  static getHistory(id: string): Promise<ReviewHistoryEntry[]> {
    return this.Http.get<unknown>(`/review/cards/${id}/history`)
      .then(this.getData)
      .then(normalizeHistory);
  }

  /** Admin-only: every authored flashcard template (the "cards stored").
   *  With no real param the GET stays unparameterized, so today's handler keeps
   *  returning the raw array. Sending `q`, `layout`, `subjectId`, `tag`, `page`
   *  or `pageSize` is what flips the route into the paginated envelope. */
  static listTemplates(params?: ListTemplatesParams): Promise<ReviewCardsPage> {
    const query = templatesQuery(params);
    const request = query
      ? this.Http.get<unknown>('/review/cards/templates', { params: query })
      : this.Http.get<unknown>('/review/cards/templates');
    return request.then(this.getData).then((raw) => this.normalizeTemplatesPage(raw));
  }

  /** Both success bodies of `GET /review/cards/templates`, one page. Public so a
   *  later spec can call it without HTTP. */
  static normalizeTemplatesPage(raw: unknown): ReviewCardsPage {
    if (Array.isArray(raw)) return pageFromEveryTemplate(raw);
    if (!isPlainObject(raw)) return emptyTemplatesPage();
    const items = Array.isArray(raw.items) ? asTemplateRows(raw.items) : [];
    const total = readNumber(raw.total) ?? items.length;
    return {
      items,
      page: readNumber(raw.page) ?? 1,
      pageSize: readNumber(raw.pageSize) ?? items.length,
      total,
      typeCounts: resolveTypeCounts(raw.typeCounts, items, total),
      serverPaged: true,
    };
  }

  /** Edit a card's content in place — keeps its id and scheduling. */
  static updateCard(id: string, body: UpdateCardBody) {
    return this.Http.put<{ updated: boolean }>(`/review/cards/${id}`, body).then(this.getData);
  }

  static deleteCard(id: string) {
    return this.Http.delete(`/review/cards/${id}`).then(this.getData);
  }

  /** One batch. `notAffected` stays a list: a partial result is not a success flag.
   *  An empty card list is refused by the API (`400`); that rejection propagates.
   *  `affected` on the wire is a count — see `BulkActionResult.affectedCount`. */
  static bulk(body: BulkCardsRequest): Promise<BulkActionResult> {
    return this.Http.post<unknown>('/review/cards/bulk', body)
      .then(this.getData)
      .then(normalizeBulkResult);
  }

  /** Admin-only: remove duplicate materialized flashcard copies on demand,
   *  correcting review counters without waiting for a server restart. */
  static dedupeTemplates() {
    return this.Http.post<{ deleted: number }>('/review/dedupe').then(this.getData);
  }

  /** Aggregated over [from, to]. A measure that did not arrive stays absent, so a
   *  screen can show it as indisponível. Null is not turned into 0. HTTP errors
   *  propagate. */
  static getSessionSummary(params: SessionSummaryParams): Promise<ReviewSessionSummary> {
    return this.Http.get<unknown>('/review/session/summary', {
      params: { from: params.from, to: params.to },
    })
      .then(this.getData)
      .then(normalizeSessionSummary);
  }

  /** The three selection dimensions that have at least one review card for the
   *  user. The route always sends all three as arrays, but a partial or
   *  malformed body must not throw — each list is read independently and a
   *  missing one becomes empty, never an absent key on the returned object. */
  static getFacets(): Promise<ReviewFacets> {
    return this.Http.get<unknown>('/review/facets').then(this.getData).then(normalizeFacets);
  }

  static getStats() {
    return this.Http.get<ReviewStats>('/review/stats').then(this.getData);
  }

  static getGlobalStats() {
    return this.Http.get('/review/stats/global').then(this.getData);
  }
}

/** A body that isn't a plain object (network hiccup turned into `""`, or a
 *  shape nobody expected) degrades to the all-empty facets, same as a body
 *  missing all three lists would. */
function normalizeFacets(raw: unknown): ReviewFacets {
  if (!isPlainObject(raw)) return { subjects: [], tags: [], decks: [] };
  return {
    subjects: asSubjectCounts(raw.subjects),
    tags: asDeckCounts(raw.tags),
    decks: asDeckCounts(raw.decks),
  };
}

/** An entry with no usable id or name can't become a selectable option, so
 *  it is dropped rather than kept as a blank one. A non-numeric count reads
 *  as 0, never NaN on screen. */
function asSubjectCounts(raw: unknown): SubjectCount[] {
  if (!Array.isArray(raw)) return [];
  const subjects: SubjectCount[] = [];
  for (const item of raw) {
    if (!isPlainObject(item)) continue;
    const { id, name } = item;
    if (!isNonEmptyString(id) || !isNonEmptyString(name)) continue;
    subjects.push({ id, name, count: readNumber(item.count) ?? 0 });
  }
  return subjects;
}

/** Same drop-if-unnamed, 0-not-NaN rule as `asSubjectCounts`, for the two
 *  facets (tags, decks) that carry no id. */
function asDeckCounts(raw: unknown): DeckCount[] {
  if (!Array.isArray(raw)) return [];
  const decks: DeckCount[] = [];
  for (const item of raw) {
    if (!isPlainObject(item)) continue;
    const { name } = item;
    if (!isNonEmptyString(name)) continue;
    decks.push({ name, count: readNumber(item.count) ?? 0 });
  }
  return decks;
}

function normalizeSessionSummary(raw: unknown): ReviewSessionSummary {
  if (!isPlainObject(raw)) return {};
  const summary: ReviewSessionSummary = {};
  // Wire names first (`cards`, `elapsedMs`, `accuracy`, `accuracyDelta`), then the
  // names on ReviewSessionSummary, so a body written either way lands on one shape.
  // Null stays absent: an empty window sends `accuracy: null` and `elapsedMs: null`,
  // and that is indisponível, not zero.
  putNumber(summary, 'cardsReviewed', raw.cards ?? raw.cardsReviewed);
  putNumber(summary, 'timeSpentMs', raw.elapsedMs ?? raw.timeSpentMs);
  putNumber(summary, 'retention', raw.accuracy ?? raw.retention);
  putNumber(summary, 'streak', raw.streak);
  putNumber(summary, 'retentionDeltaVsYesterday', raw.accuracyDelta ?? raw.retentionDeltaVsYesterday);

  const ratingCounts = copyRatingCounts(raw.ratings) ?? copyRatingCounts(raw.ratingCounts);
  if (ratingCounts) summary.ratingCounts = ratingCounts;
  const streakRecord = raw.streakRecord ?? raw.isStreakRecord;
  if (typeof streakRecord === 'boolean') summary.isStreakRecord = streakRecord;
  // The wire key is `reinforce`, and it is present and empty when the window had
  // no "again". Null or a missing field stays absent: the errors are not known yet.
  const reinforce = Array.isArray(raw.reinforce)
    ? raw.reinforce
    : Array.isArray(raw.reinforceCards)
      ? raw.reinforceCards
      : undefined;
  if (reinforce) summary.reinforceCards = normalizeReinforce(reinforce);
  return summary;
}

function putNumber(
  summary: ReviewSessionSummary,
  key: 'cardsReviewed' | 'timeSpentMs' | 'retention' | 'streak' | 'retentionDeltaVsYesterday',
  value: unknown
) {
  const n = readNumber(value);
  if (n !== undefined) summary[key] = n;
}

function copyRatingCounts(raw: unknown): ReviewSessionSummary['ratingCounts'] {
  if (!isPlainObject(raw)) return undefined;
  const counts: Partial<Record<Rating, number>> = {};
  for (const key of RATINGS) {
    const value = raw[key];
    if (typeof value === 'number') counts[key] = value;
  }
  if (Object.keys(counts).length === 0) return undefined;
  // A rating that did not arrive stays off this object. Filling it with 0 would
  // read as "none of that rating" when the measure is simply absent.
  return counts as Record<Rating, number>;
}

function normalizeHistory(raw: unknown): ReviewHistoryEntry[] {
  const list = Array.isArray(raw)
    ? raw
    : isPlainObject(raw) && Array.isArray(raw.history)
      ? raw.history
      : isPlainObject(raw) && Array.isArray(raw.entries)
        ? raw.entries
        : [];
  const entries: ReviewHistoryEntry[] = [];
  for (const item of list) {
    const entry = normalizeHistoryEntry(item);
    if (entry) entries.push(entry);
  }
  return entries;
}

/** `elapsedMs: null` means the duration was not stored. It stays off the entry
 *  so a caller can tell "not recorded" from a real zero. */
function normalizeHistoryEntry(raw: unknown): ReviewHistoryEntry | undefined {
  if (!isPlainObject(raw)) return undefined;
  const rating = raw.rating;
  const at = raw.at;
  if (!isRating(rating) || typeof at !== 'string') return undefined;
  const entry: ReviewHistoryEntry = { rating, at };
  if (typeof raw.elapsedMs === 'number') entry.elapsedMs = raw.elapsedMs;
  return entry;
}

function isRating(value: unknown): value is Rating {
  return value === 'again' || value === 'hard' || value === 'good' || value === 'easy';
}

function normalizeReinforce(raw: unknown[]): NonNullable<ReviewSessionSummary['reinforceCards']> {
  const cards: NonNullable<ReviewSessionSummary['reinforceCards']> = [];
  for (const item of raw) {
    if (!isPlainObject(item) || typeof item.id !== 'string') continue;
    const label = typeof item.label === 'string' ? item.label : typeof item.front === 'string' ? item.front : '';
    const card: (typeof cards)[number] = {
      id: item.id,
      label,
      subjectId: typeof item.subjectId === 'string' ? item.subjectId : '',
    };
    if (item.layout === 'basic' || item.layout === 'single' || item.layout === 'occlusion') {
      card.layout = item.layout;
    }
    const shortId = typeof item.shortId === 'number' ? item.shortId : typeof item.number === 'number' ? item.number : undefined;
    if (shortId !== undefined) card.shortId = shortId;
    cards.push(card);
  }
  return cards;
}

/** Only keys the caller actually set. Undefined, '' and null stay off the query,
 *  and a `q` that trims to nothing is omitted, so the parameter-less GET keeps
 *  today's raw-array response. The search key on the wire is `q`, not `search`. */
function templatesQuery(params?: ListTemplatesParams): Record<string, string | number> | undefined {
  if (!params) return undefined;
  const query: Record<string, string | number> = {};
  if (typeof params.q === 'string') {
    const q = params.q.trim();
    if (q !== '') query.q = q;
  }
  if (isNonEmptyString(params.layout)) query.layout = params.layout;
  if (isNonEmptyString(params.subjectId)) query.subjectId = params.subjectId;
  if (isNonEmptyString(params.tag)) query.tag = params.tag;
  if (typeof params.page === 'number') query.page = params.page;
  if (typeof params.pageSize === 'number') query.pageSize = params.pageSize;
  return Object.keys(query).length > 0 ? query : undefined;
}

/** The whole catalog, unsliced. The caller still has every card and can paginate
 *  in memory — this is the degradation while the server ignores pagination. */
function asTemplateRows(raw: unknown[]): TemplateRow[] {
  const items: TemplateRow[] = [];
  for (const item of raw) {
    const row = normalizeTemplateRow(item);
    if (row) items.push(row);
  }
  return items;
}

/** Maps the listing's wire fields onto `ReviewCardAdminRow` without dropping a
 *  card that still speaks the old names.
 *  `number` → `shortId`. `successRate: null` leaves `accuracy.rate` absent
 *  (indisponível) while `answerCount` still lands on `sampleSize`.
 *  `possibleDuplicate` + `duplicateOfId` → `probableDuplicate`. */
function normalizeTemplateRow(raw: unknown): TemplateRow | undefined {
  if (!isPlainObject(raw)) return undefined;
  const row: TemplateRow = { ...(raw as unknown as TemplateRow) };
  if (row.shortId === undefined && typeof raw.number === 'number') row.shortId = raw.number;
  if (row.accuracy === undefined) {
    const rate = raw.successRate;
    const answerCount = raw.answerCount;
    if (typeof rate === 'number' || typeof answerCount === 'number') {
      row.accuracy = {};
      if (typeof rate === 'number') row.accuracy.rate = rate;
      if (typeof answerCount === 'number') row.accuracy.sampleSize = answerCount;
    }
  }
  if (
    row.probableDuplicate === undefined &&
    raw.possibleDuplicate === true &&
    typeof raw.duplicateOfId === 'string'
  ) {
    row.probableDuplicate = { cardId: raw.duplicateOfId };
    if (typeof raw.duplicateOfNumber === 'number') {
      row.probableDuplicate.shortId = raw.duplicateOfNumber;
    }
  }
  return row;
}

function pageFromEveryTemplate(raw: unknown[]): ReviewCardsPage {
  const items = asTemplateRows(raw);
  const total = items.length;
  return {
    items,
    page: 1,
    pageSize: total,
    total,
    typeCounts: typeCountsFromItems(items),
    serverPaged: false,
  };
}

function emptyTemplatesPage(): ReviewCardsPage {
  return {
    items: [],
    page: 1,
    pageSize: 0,
    total: 0,
    typeCounts: { basic: 0, single: 0, occlusion: 0 },
    serverPaged: false,
  };
}

function resolveTypeCounts(
  rawCounts: unknown,
  items: TemplateRow[],
  total: number
): Record<CardLayout, number> {
  const provided = providedTypeCounts(rawCounts);
  if (provided) return provided;
  // This page holds every row `total` names, so the counts can be derived the
  // same way as a raw array. They sum to total.
  if (total === items.length) return typeCountsFromItems(items);
  // The counts did not arrive and this page is only part of the catalog.
  // These zeros are not "no cards of that type" — counting a partial page
  // would under-count the types that landed on other pages.
  return { basic: 0, single: 0, occlusion: 0 };
}

function providedTypeCounts(raw: unknown): Record<CardLayout, number> | undefined {
  if (!isPlainObject(raw)) return undefined;
  const basic = raw.basic;
  const single = raw.single;
  const occlusion = raw.occlusion;
  if (typeof basic !== 'number' || typeof single !== 'number' || typeof occlusion !== 'number') {
    return undefined;
  }
  return { basic, single, occlusion };
}

/** Missing layout, and anything other than `single` or `occlusion`, counts as
 *  basic — the product's "otherwise Texto" rule. The three counts sum to the
 *  length of `items`. */
function typeCountsFromItems(items: readonly unknown[]): Record<CardLayout, number> {
  const typeCounts: Record<CardLayout, number> = { basic: 0, single: 0, occlusion: 0 };
  for (const item of items) {
    const layout = isPlainObject(item) ? item.layout : undefined;
    if (layout === 'single' || layout === 'occlusion') typeCounts[layout] += 1;
    else typeCounts.basic += 1;
  }
  return typeCounts;
}

function normalizeBulkResult(raw: unknown): BulkActionResult {
  const body = isPlainObject(raw) ? raw : undefined;
  // `{"affected": 2}` is a count. An array is the ids. A count does not invent ids:
  // `affected` stays [] and `affectedCount` carries the number.
  const listed = stringList(body?.affected);
  const affectedCount = typeof body?.affected === 'number' ? body.affected : listed.length;
  return {
    affected: listed,
    affectedCount,
    notAffected: failureList(body?.unaffected ?? body?.notAffected),
  };
}

/** The field is an array of strings, or it is []. A mixed list is not that, so
 *  it does not become a partial list of the strings that happened to parse. */
function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') return [];
    ids.push(item);
  }
  return ids;
}

function failureList(value: unknown): BulkActionResult['notAffected'] {
  if (!Array.isArray(value)) return [];
  const failures: BulkActionResult['notAffected'] = [];
  for (const entry of value) {
    if (!isPlainObject(entry)) continue;
    const cardId = typeof entry.cardId === 'string' ? entry.cardId : entry.id;
    const reason = entry.reason;
    if (typeof cardId !== 'string' || typeof reason !== 'string') continue;
    failures.push({ cardId, reason });
  }
  return failures;
}

export default ReviewService;
